import React, { FC, useEffect, useState, useMemo } from 'react';
import { Modal } from 'antd';
import useAxios from '../../../../hooks/useAxios';
import Loader from '../../../../components/Loader';
import ErrorResponse from '../../../../components/ErrorResponse';
import { getClassElearningBadWord } from '../../../../api/campusClasses.api';
import { IGetElearningBadWord, IElearningBadWordItem } from '../../../../models/eLearning.model';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Block = styled.div`

  .profanity-chat-content .color-red {
    color: #ff4d4f;
    font-weight: 600;
  }

  .profanity-chat-content {
    background: #fafbfc;
    border-radius: 8px;
    padding: 24px;
    margin-top: 16px;
    margin-bottom: 8px;
    border: 1px solid #eee;
    min-height: 120px;
    height: 300px;
    max-height: 300px;
    overflow-y: auto;
  }

  .profanity-chat-paging {
    text-align: center;
    margin-top: 24px;
  }

  .profanity-chat-button {
    margin-right: 16px;
    padding: 4px 16px;
    border-radius: 4px;
    border: 1px solid #ccc;
  }  

`;

interface IProps {
  visible: boolean;
  onCancel: () => void;
  modalParams?: {
    classCode: string;
    semesterCode: string;
    memberCode: string;
  };
}

const fnProfanityHighlight = (originalText: string, filteredText: string) : React.ReactNode[] => {
  if (!originalText || !filteredText || originalText === filteredText || originalText.length !== filteredText.length) {
    return [originalText];
  }

  // TextHelper의 범위 계산 재사용
  const ranges = findFilteredRanges(originalText, filteredText);

  if (ranges.length === 0) {
    return [originalText];
  }

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const { start, end, text } of ranges) {
    if (start > lastIndex) {
      result.push(originalText.slice(lastIndex, start));
    }

    result.push(
      <span key={`highlight-${start}`} className={'color-red'}>
        {text}
      </span>
    );

    lastIndex = end;
  }

  if (lastIndex < originalText.length) {
    result.push(originalText.slice(lastIndex));
  }

  return result;
}

const findFilteredRanges = (original: string, filtered: string) => {
  const ranges = [];
  let currentRange = null;

  for (let i = 0; i < original.length; i++) {
    const isFiltered = filtered[i] === "*";

    if (isFiltered) {
      if (!currentRange) {
        currentRange = { start: i, end: i + 1, text: original[i] };
      } else {
        currentRange.end = i + 1;
        currentRange.text += original[i];
      }
    } else {
      if (currentRange) {
        ranges.push(currentRange);
        currentRange = null;
      }
    }
  }

  // 마지막 범위 처리
  if (currentRange) {
    ranges.push(currentRange);
  }

  return ranges;
}

const ClassElearningBadWord: FC<IProps> = ({
  visible,
  onCancel,
  modalParams,
}) => {
  const { t } = useTranslation();
  const { data: badWordRes, error: badWordError, isLoading: badWordLoading, execute: executeBadWord } = useAxios<IGetElearningBadWord>();
  const [badWordData, setBadWordData] = useState<IElearningBadWordItem[]>();

  useEffect(() => {
    if (visible && modalParams) {
      executeBadWord(getClassElearningBadWord(modalParams));
    }
  }, [visible, modalParams]);

  useEffect(() => {
    if(badWordRes) {
      setBadWordData(badWordRes?.data);
    }
  }, [badWordRes]);

  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [visible]); // 모달 열릴 때마다 첫 페이지로
  
  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      title={null}
      className="inappropriate-modal"
      centered
      style={{
        padding: 0,
      }}
      bodyStyle={{
        padding: 0,
        margin: 0,
        background: '#fff',
      }}
      maskStyle={{
        background: 'rgba(0,0,0,0.2)'
      }}
    >
      <Block>
      <div
        className="ant-modal-content"
        style={{
          boxShadow: 'none',
          border: 'none',
          margin: 0,
          padding: 0,
          background: '#fff',
        }}
      >
        <div className="ant-modal-header">
          <div className="ant-modal-title">Inappropriate Language Detected</div>
        </div>
        <div className="ant-modal-body">
          {badWordLoading ? (
            <Loader />
          ) : badWordError ? (
            <ErrorResponse error={badWordError} />
          ) : (
            <>
              <div className="comm-table__area mt-0">
                <table>
                  <colgroup>
                    <col width="33.34%" />
                    <col width="33.34%" />
                    <col width="33.34%" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Topic</th>
                      <th>Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{badWordData?.[page]?.memberName}{badWordData?.[page]?.memberEngName ? ` (${badWordData?.[page]?.memberEngName})` : ''}</td>
                      <td>{badWordData?.[page]?.topic || '-'}</td>
                      <td>{badWordData?.[page]?.activityName || '-'}</td>
                      
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* chat 내용 */}
              <div
                className="profanity-chat-content"
              >
                {(badWordData?.[page]?.scripts.length || 0) > 0 ? (
                          badWordData?.[page]?.scripts.map((s, idx, arr) => {
                            const messageContent = (s.chatRole === 'US' && s.filteredMessageContent) ? fnProfanityHighlight(
                              s.messageContent,
                              s.filteredMessageContent
                            ) : s.messageContent;
                            
                            return (
                              <div key={idx} style={{ marginBottom: 8, lineHeight: 1.7 }}>
                                <span style={{ fontWeight: 'bold' }}>
                                  {s.chatRole === 'AI' ? 'CANBOT' : 'Student'} :&nbsp;
                                </span>
                                <span style={{ marginLeft: 4 }}>{messageContent}</span> 
                              </div>
                            );
                          })
                        ) : (
                          <dl className="dailymission-script-list empty-list"></dl>
                        )}
              </div>
              {/* 페이지네이션: 대화 박스 바깥, 맨 하단 중간 정렬 */}
              {(badWordData?.length || 0) > 1 && (
                <div className="profanity-chat-paging">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="profanity-chat-button"
                    style={{  background: page === 0 ? '#f5f5f5' : '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer' }}
                  >
                    {t('common.button.back')}
                  </button>
                  <span style={{ fontWeight: 500, margin: '0 12px' }}>{page + 1} / {badWordData?.length}</span>
                  <button
                    onClick={() => setPage(p => Math.min((badWordData?.length || 0) - 1, p + 1))}
                    disabled={page === (badWordData?.length || 0) - 1}
                    className="profanity-chat-button"
                    style={{ background: page === (badWordData?.length || 0) - 1 ? '#f5f5f5' : '#fff', cursor: page === (badWordData?.length || 0) - 1 ? 'not-allowed' : 'pointer' }}
                  >
                    {t('common.button.next')}
                  </button>
                </div>
              )}
             
            </>
          )}
          </div>
        </div>
      </Block>
    </Modal>
  );
};

export default ClassElearningBadWord; 